import React, { useState, useEffect } from 'react';
import { Store, Users, DollarSign, Shield, Save, ToggleLeft, Award, Percent } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

const MarketplaceSettings = () => {
  const [settings, setSettings] = useState({
    vendor_mode_enabled: false,
    show_become_vendor: false,
    show_vendor_benefits: false,
    // Normal Plan
    normal_commission_percent: 8,
    normal_monthly_fee: 0,
    normal_max_products: 50,
    // Gold Plan
    gold_commission_percent: 6,
    gold_monthly_fee: 4.99,
    gold_max_products: 200
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axiosInstance.get('/settings/marketplace');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axiosInstance.put('/settings/marketplace', settings);
      toast.success('Einstellungen gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5EDE4' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8B7355]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 px-3" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-3xl mx-auto">
        <AdminPageHeader 
          title="Marktplatz-Einstellungen" 
          subtitle="Verkäufer-Funktionen und Multi-Vendor-Modus verwalten"
          icon={Store}
        />

        {/* Hauptschalter */}
        <Card className="rounded-none border mb-4" style={{ borderColor: '#C4A084' }}>
          <CardHeader className="pb-2" style={{ backgroundColor: '#AF9774' }}>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <ToggleLeft className="h-5 w-5" />
              Verkäufer-Modus
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="p-3 rounded-none" style={{ backgroundColor: '#FFF8E8', border: '1px solid #E8D090' }}>
              <p className="text-sm text-amber-800">
                <strong>Hinweis:</strong> Wenn der Verkäufer-Modus ausgeschaltet ist, ist Ihr Shop ein reiner Einzelhändler-Shop. 
                Keine Verkäufer-Registrierung oder Multi-Vendor-Funktionen werden angezeigt.
              </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-none">
              <div>
                <Label className="font-semibold text-lg">Multi-Vendor Marktplatz aktivieren</Label>
                <p className="text-sm text-gray-500">Ermöglicht anderen Verkäufern, sich anzumelden und zu verkaufen</p>
              </div>
              <Switch
                checked={settings.vendor_mode_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, vendor_mode_enabled: checked })}
              />
            </div>

            {settings.vendor_mode_enabled && (
              <>
                <div className="flex items-center justify-between p-3 border-l-4" style={{ borderColor: '#AF9774', backgroundColor: '#F5EDE4' }}>
                  <div>
                    <Label>"Verkäufer werden" auf Homepage anzeigen</Label>
                    <p className="text-xs text-gray-500">Button und Bereich auf der Startseite</p>
                  </div>
                  <Switch
                    checked={settings.show_become_vendor}
                    onCheckedChange={(checked) => setSettings({ ...settings, show_become_vendor: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border-l-4" style={{ borderColor: '#AF9774', backgroundColor: '#F5EDE4' }}>
                  <div>
                    <Label>Verkäufer-Vorteile auf Homepage anzeigen</Label>
                    <p className="text-xs text-gray-500">Vorteile-Liste für potentielle Verkäufer</p>
                  </div>
                  <Switch
                    checked={settings.show_vendor_benefits}
                    onCheckedChange={(checked) => setSettings({ ...settings, show_vendor_benefits: checked })}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Abo-Pläne */}
        {settings.vendor_mode_enabled && (
          <>
            {/* Normal Plan */}
            <Card className="rounded-none border mb-4" style={{ borderColor: '#C4A084' }}>
              <CardHeader className="pb-2" style={{ backgroundColor: '#AF9774' }}>
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Normal-Abo (Basis)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Provision (%)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        value={settings.normal_commission_percent}
                        onChange={(e) => setSettings({ ...settings, normal_commission_percent: parseFloat(e.target.value) || 0 })}
                        min="0"
                        max="100"
                        step="0.5"
                        className="rounded-none"
                      />
                      <Percent className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <Label>Monatliche Gebühr (€)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        value={settings.normal_monthly_fee}
                        onChange={(e) => setSettings({ ...settings, normal_monthly_fee: parseFloat(e.target.value) || 0 })}
                        min="0"
                        step="0.01"
                        className="rounded-none"
                      />
                      <span className="text-gray-400">€</span>
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Max. Produkte</Label>
                  <Input
                    type="number"
                    value={settings.normal_max_products}
                    onChange={(e) => setSettings({ ...settings, normal_max_products: parseInt(e.target.value) || 0 })}
                    min="1"
                    className="rounded-none mt-1"
                  />
                </div>

                {/* Preview */}
                <div className="p-3 rounded-none bg-gray-50">
                  <p className="text-sm font-medium">Vorschau auf Homepage:</p>
                  <div className="mt-2 p-3 bg-white border rounded">
                    <p className="font-bold">Normal-Abo</p>
                    <p className="text-sm text-gray-600">
                      {settings.normal_monthly_fee > 0 ? `${formatEUR(settings.normal_monthly_fee)}/Monat` : 'Kostenlos'}
                    </p>
                    <ul className="text-sm mt-2 space-y-1">
                      <li>✓ {settings.normal_commission_percent}% Provision</li>
                      <li>✓ Bis zu {settings.normal_max_products} Produkte</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Gold Plan */}
            <Card className="rounded-none border mb-4" style={{ borderColor: '#D4AF37' }}>
              <CardHeader className="pb-2" style={{ backgroundColor: '#D4AF37' }}>
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  Gold-Abo (Premium)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Provision (%)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        value={settings.gold_commission_percent}
                        onChange={(e) => setSettings({ ...settings, gold_commission_percent: parseFloat(e.target.value) || 0 })}
                        min="0"
                        max="100"
                        step="0.5"
                        className="rounded-none"
                      />
                      <Percent className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <Label>Monatliche Gebühr (€)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        value={settings.gold_monthly_fee}
                        onChange={(e) => setSettings({ ...settings, gold_monthly_fee: parseFloat(e.target.value) || 0 })}
                        min="0"
                        step="0.01"
                        className="rounded-none"
                      />
                      <span className="text-gray-400">€</span>
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Max. Produkte</Label>
                  <Input
                    type="number"
                    value={settings.gold_max_products}
                    onChange={(e) => setSettings({ ...settings, gold_max_products: parseInt(e.target.value) || 0 })}
                    min="1"
                    className="rounded-none mt-1"
                  />
                </div>

                {/* Preview */}
                <div className="p-3 rounded-none" style={{ backgroundColor: '#FFF8E0' }}>
                  <p className="text-sm font-medium">Vorschau auf Homepage:</p>
                  <div className="mt-2 p-3 bg-white border-2 rounded" style={{ borderColor: '#D4AF37' }}>
                    <p className="font-bold flex items-center gap-2">
                      <Award className="h-4 w-4 text-yellow-500" />
                      Gold-Abo
                    </p>
                    <p className="text-sm text-gray-600">{formatEUR(settings.gold_monthly_fee)}/Monat</p>
                    <ul className="text-sm mt-2 space-y-1">
                      <li>✓ Nur {settings.gold_commission_percent}% Provision</li>
                      <li>✓ Bis zu {settings.gold_max_products} Produkte</li>
                      <li>✓ Gold-Badge & bevorzugte Platzierung</li>
                      <li>✓ Individuelle Shop-Gestaltung</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Speichern */}
        <Button 
          onClick={saveSettings}
          disabled={saving}
          className="w-full rounded-none"
          style={{ backgroundColor: '#AF9774' }}
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Wird gespeichert...' : 'Einstellungen speichern'}
        </Button>
      </div>
    </div>
  );
};

export default MarketplaceSettings;
